import { Center, Loader, ScrollArea, Skeleton, Stack, Text } from '@mantine/core';
import { DateDivider, groupMessages } from '../../lib/chat-format';
import { MessageBubble } from './MessageBubble';
import { THREAD_BG, type Seq } from './MetaBubbles';
import { useThreadScroll } from './useThreadScroll';
import type { ChannelCapabilities, InboxMessage } from './types';

/** Scrollable message thread: day groups + same-direction bursts + bubbles.
 *  Keyset infinite scroll — older pages auto-load as the top sentinel scrolls
 *  into view, the read position is preserved on prepend, and the view follows
 *  the newest message when at the bottom (all via {@link useThreadScroll}).
 *  Channel-agnostic. */
export function MessageThread({
  messages,
  capabilities,
  isPending,
  hasMore,
  loadOlder,
  loadingOlder,
  conversationKey,
}: {
  messages: InboxMessage[];
  capabilities: ChannelCapabilities;
  isPending: boolean;
  hasMore?: boolean;
  loadOlder?: () => void;
  loadingOlder?: boolean;
  /** Switches when the open conversation changes — resets scroll to newest. */
  conversationKey?: number | string | null;
}) {
  const { viewportRef, topSentinelRef, onScroll } = useThreadScroll({
    messageCount: messages.length,
    newestId: messages.length ? (messages[messages.length - 1]?.id ?? null) : null,
    oldestId: messages.length ? (messages[0]?.id ?? null) : null,
    hasMore: !!hasMore,
    isFetchingOlder: !!loadingOlder,
    fetchOlder: loadOlder ?? (() => {}),
    conversationKey: conversationKey ?? null,
  });

  if (isPending) return <Skeleton height={400} m="md" />;

  const grouped = groupMessages(messages);

  return (
    <ScrollArea
      style={{ flex: 1, padding: 12, background: THREAD_BG[capabilities.channel] }}
      viewportRef={viewportRef}
      onScrollPositionChange={onScroll}
    >
      {/* Top sentinel — scrolling it into view lazy-loads the next older page. */}
      <div ref={topSentinelRef} />
      {loadingOlder && (
        <Center mb="xs">
          <Loader size="xs" />
        </Center>
      )}
      <Stack gap={4}>
        {grouped.map((g) => (
          <div key={g.label}>
            <DateDivider label={g.label} />
            {g.bursts.map((burst, bi) => (
              <Stack key={bi} gap={2} mb={6}>
                {burst.messages.map((m, mi) => {
                  const n = burst.messages.length;
                  const seq: Seq =
                    n === 1 ? 'single' : mi === 0 ? 'first' : mi === n - 1 ? 'last' : 'mid';
                  return <MessageBubble key={m.id} m={m} capabilities={capabilities} seq={seq} />;
                })}
              </Stack>
            ))}
          </div>
        ))}
        {messages.length === 0 && (
          <Text c="dimmed" ta="center" py="xl">
            No messages yet.
          </Text>
        )}
      </Stack>
    </ScrollArea>
  );
}
